import { ReactNode } from 'react';
// eslint-disable-next-line camelcase
import { findChildCareProviders_findChildCareProviders_ProviderSearchSuccess_providers } from '@/__generated__/findChildCareProviders';
import {
  CareAttributesCC,
  ChildCareAgeRangeType,
  Gender,
  ServiceType,
  SubServiceType,
  TourRequestStatus,
} from '@/__generated__/globalTypes';
import { CARE_DATES, DAY_CARE_COST_IMPORTANCE } from '@/constants';
// eslint-disable-next-line camelcase
import { ListChildCareProvidersExpanded_listChildCareProvidersExpanded_ProvidersExpandedSearchSuccess_providerSearchResults_provider } from '@/__generated__/ListChildCareProvidersExpanded';
import { PhoneVerificationChannel } from '@care/provider-react-components';
import { CCCaregiverAttributes, DayOfWeek, Location, OptionalReducerData } from './common';

// Daycare Kinds
export enum DayCareKind {
  DAY_CARE_CENTERS = 'DAY_CARE_CENTERS',
  PRESCHOOL_CENTERS = 'PRESCHOOL_CENTERS',
  AFTER_SCHOOL_CENTERS = 'AFTER_SCHOOL_CENTERS',
}

export const DayCareKindLabels: { [key in DayCareKind]: string } = {
  [DayCareKind.DAY_CARE_CENTERS]: 'Daycare centers',
  [DayCareKind.PRESCHOOL_CENTERS]: 'Preschool centers',
  [DayCareKind.AFTER_SCHOOL_CENTERS]: 'After school care',
};
// End Daycare Kinds

// Default Care Kinds
export enum DefaultCareKind {
  NANNIES_RECURRING_BABYSITTERS = 'NANNIES_RECURRING_BABYSITTERS',
  ONE_TIME_BABYSITTERS = 'ONE_TIME_BABYSITTERS',
  DAY_CARE_CENTERS = 'DAY_CARE_CENTERS',
  CHILDCARE = 'CHILDCARE',
}

export enum CampsCareKind {
  CAMPS = 'CAMPS',
}

export const CampsCareKindLabel: { [key in CampsCareKind]: string } = {
  [CampsCareKind.CAMPS]: 'Activities & camps',
};

export const CampsCareKindDescriptions: { [key in CampsCareKind]: string } = {
  [CampsCareKind.CAMPS]: 'Book art classes, after-school programs or camps near you',
};

export const CareKindLabels: { [key in DefaultCareKind]: string } = {
  [DefaultCareKind.NANNIES_RECURRING_BABYSITTERS]: 'Nannies / recurring sitters',
  [DefaultCareKind.ONE_TIME_BABYSITTERS]: 'One-time sitters',
  [DefaultCareKind.DAY_CARE_CENTERS]: 'Daycare centers',
  [DefaultCareKind.CHILDCARE]: 'Other child care',
};

export const CareKindDescriptions: { [key in DefaultCareKind]: string } = {
  [DefaultCareKind.NANNIES_RECURRING_BABYSITTERS]:
    'Hire for regular hours, ongoing care, full or part-time',
  [DefaultCareKind.ONE_TIME_BABYSITTERS]:
    'Instantly book for events, occasional plans or backup care',
  [DefaultCareKind.DAY_CARE_CENTERS]: 'Find daycares, learning centers, or pre-schools near you',
  [DefaultCareKind.CHILDCARE]: 'Explore mother’s helpers, night nannies and more',
};

export const SeekerOverhaulCCM1Labels: { [key in DefaultCareKind]: string } = {
  [DefaultCareKind.ONE_TIME_BABYSITTERS]: 'Babysitter',
  [DefaultCareKind.DAY_CARE_CENTERS]: 'Daycare centers',
  [DefaultCareKind.NANNIES_RECURRING_BABYSITTERS]: 'Nanny',
  [DefaultCareKind.CHILDCARE]: 'Other child care',
};

export const SeekerOverhaulCCM1V2Descriptions: { [key in DefaultCareKind]: string } = {
  [DefaultCareKind.ONE_TIME_BABYSITTERS]: `Book sitters as you need them for children of all ages.`,
  [DefaultCareKind.DAY_CARE_CENTERS]: `Learn more about pre-school, Montessori and learning center options.`,
  [DefaultCareKind.NANNIES_RECURRING_BABYSITTERS]: `Find a full-time or part-time nanny for ongoing care needs.`,
  [DefaultCareKind.CHILDCARE]: `Explore additional types of child care such as mother's helper, night nanny and more.`,
};

export const CareKindToSubServiceType: {
  [key in CareKind]: SubServiceType;
} = {
  [DefaultCareKind.NANNIES_RECURRING_BABYSITTERS]: SubServiceType.NANNY_OR_BABYSITTER,
  [DefaultCareKind.ONE_TIME_BABYSITTERS]: SubServiceType.ONE_TIME_BABYSITTER,
  [DefaultCareKind.DAY_CARE_CENTERS]: SubServiceType.DAY_CARE,
  [DefaultCareKind.CHILDCARE]: SubServiceType.CHILDCARE,
  [DayCareKind.PRESCHOOL_CENTERS]: SubServiceType.DAY_CARE,
  [DayCareKind.AFTER_SCHOOL_CENTERS]: SubServiceType.DAY_CARE,
  [CampsCareKind.CAMPS]: SubServiceType.CAMPS,
};

// End Default Care Kinds

export type CareKind = DefaultCareKind | DayCareKind | CampsCareKind;

export enum CareDayTime {
  MORNING = 'MORNING',
  AFTERNOON = 'AFTERNOON',
  ALLDAY = 'ALLDAY',
}

export const CareDayTimeLabels: { [key in CareDayTime]: string } = {
  [CareDayTime.MORNING]: 'Morning',
  [CareDayTime.AFTERNOON]: 'Afternoon',
  [CareDayTime.ALLDAY]: 'All day',
};

export enum IdealCaregiver {
  PATIENT = 'PATIENT',
  ENERGETIC = 'ENERGETIC',
  LOVING = 'LOVING',
  RELIABLE = 'RELIABLE',
  CARING = 'CARING',
  RESPONSIBLE = 'RESPONSIBLE',
}

export enum NeedHelpWith {
  HOMEWORK_HELP = 'HOMEWORK_HELP',
  STRUCTURED_ACTIVITIES = 'STRUCTURED_ACTIVITIES',
  LIGHT_HOUSEKEEPING = 'LIGHT_HOUSEKEEPING',
}

export const CaregiverAttributesLabels: { [key in CCCaregiverAttributes]: string } = {
  [CCCaregiverAttributes.FIRST_AID_OR_CPR]: 'Has CPR / First Aid training',
  [CCCaregiverAttributes.COMFORTABLE_WITH_PETS]: 'Comfortable with pets',
  [CCCaregiverAttributes.OWN_TRANSPORTATION]: 'Has reliable transportation',
  [CCCaregiverAttributes.DOES_NOT_SMOKE]: 'Non-smoker',
  [CCCaregiverAttributes.SPECIAL_NEEDS_EXPERIENCE]: 'Special needs experience',
  [CCCaregiverAttributes.MULTILINGUAL]: 'Multilingual',
  // Unused in CaregiverAttributesLabels
  [CCCaregiverAttributes.COLLEGE_EDUCATED]: 'College educated',
  [CCCaregiverAttributes.CPR_TRAINED]: 'Has CPR / First Aid training',
  [CCCaregiverAttributes.COLLEGE_DEGREE]: 'College educated',
};

export const mapAttributesTypeToDisplay: Record<string, string> = Object.freeze({
  [CareAttributesCC.FIRST_AID_OR_CPR]: CaregiverAttributesLabels.FIRST_AID_OR_CPR,
  [CareAttributesCC.COMFORTABLE_WITH_PETS]: CaregiverAttributesLabels.COMFORTABLE_WITH_PETS,
  [CareAttributesCC.SPECIAL_NEEDS_EXPERIENCE]: CaregiverAttributesLabels.SPECIAL_NEEDS_EXPERIENCE,
  [CareAttributesCC.MULTILINGUAL]: CaregiverAttributesLabels.MULTILINGUAL,
  [CareAttributesCC.OWN_TRANSPORTATION]: CaregiverAttributesLabels.OWN_TRANSPORTATION,
});

export interface Child {
  id?: string | null;
  ageRange: ChildCareAgeRangeType;
  gender?: Gender;
  approximateDoB?: string | null;
}

export type ChildDOB = string | null;

export interface ChildDateOfBirth {
  name?: string;
  month: string;
  year: string;
}

export const DayCareFrequencyOptions = {
  PART_TIME: 'part-time',
  FULL_TIME: 'full-time',
};

type CareDays = {
  careType: string;
  specificDays: DayOfWeek[];
};

interface DayCareAutoSubmit {
  startTime: string | null;
  attempts: number;
  submitted: boolean;
}

export type SeekerContactMethod = 'MAIL' | 'PHONE';

type DayCareData = {
  careFrequency: CareDays;
  careCostImportance: DAY_CARE_COST_IMPORTANCE;
  startMonth: string | null;
  childrenDateOfBirth: ChildDateOfBirth[];
  dayTime: CareDayTime;
  additionalInformation: string;
  recommendationsTrackingId: string;
  recommendations: DaycareProviderProfile[];
  submissionCompleted: boolean;
  shouldShowMap: boolean;
  autoSubmit: DayCareAutoSubmit;
  contactMethod?: SeekerContactMethod;
  isExpanded: boolean;
};

export enum DaycareProviderLicenseType {
  fullPermit,
  // TODO other types...
}

export interface DaycareProviderProfile
  // eslint-disable-next-line camelcase
  extends findChildCareProviders_findChildCareProviders_ProviderSearchSuccess_providers {
  avgReviewRating: number;
  selected: boolean;
  hasCoordinates: boolean;
  searchMatchReason: string | null;
  leadId?: string;
  tourRequestStatus?: TourRequestStatus | null;
  tourBooked?: boolean;
}

export interface ProviderExpandedWithReason
  // The query was created using PascalCase and cannot change that on the frontend
  // eslint-disable-next-line camelcase
  extends ListChildCareProvidersExpanded_listChildCareProvidersExpanded_ProvidersExpandedSearchSuccess_providerSearchResults_provider {
  searchMatchReason: string;
}

export interface SeekerCCState {
  firstName: string;
  lastName: string;
  email: string | null;
  phoneNumber: string;
  phoneVerificationMethod: PhoneVerificationChannel | null;
  czenTimeIntent?: TimeIntent;
  czenServiceIdForMember?: ServiceIdsForMember;
  careDate: CARE_DATES;
  careCostImportance: DAY_CARE_COST_IMPORTANCE;
  careKind: CareKind;
  careChildren: Child[];
  careChildrenDOB: ChildDOB[];
  numberOfChildren: number;
  caregiverCount: number;
  careExpecting: boolean;
  showChildDetails: boolean;
  distanceLearning: boolean;
  enrollmentSource: string;
  dayCare: DayCareData;
  isSendLeadEnabled: boolean;
  isPixelFired: boolean;
  additionalVerticals: ServiceType[];
  careAttributesCC: CareAttributesCC[];
}

export enum ServiceIdsForMember {
  babysitter = 'babysitter',
  nanny = 'nanny',
  dayCare = 'dayCare',
}

export enum TimeIntent {
  rightNow = 'RightNow',
  withinOneWeek = 'WithinaWeek',
  in1to2Months = 'In1-2Months',
  justBrowsing = 'Just+browsing',
}

export enum CareKindIntent {
  onetimeSitters = 'onetimeSitters',
  nanniesRecurringSitters = 'nanniesRecurringSitters',
  daycareCenters = 'daycareCenters',
  preschoolCenters = 'preschoolCenters',
  afterSchoolCare = 'afterSchoolCare',
}

export const Months = [
  { value: 'JANUARY', label: 'January' },
  { value: 'FEBRUARY', label: 'February' },
  { value: 'MARCH', label: 'March' },
  { value: 'APRIL', label: 'April' },
  { value: 'MAY', label: 'May' },
  { value: 'JUNE', label: 'June' },
  { value: 'JULY', label: 'July' },
  { value: 'AUGUST', label: 'August' },
  { value: 'SEPTEMBER', label: 'September' },
  { value: 'OCTOBER', label: 'October' },
  { value: 'NOVEMBER', label: 'November' },
  { value: 'DECEMBER', label: 'December' },
];

export type SeekerCCAction =
  | { type: 'setCzenIntent'; intent: TimeIntent; reducer?: OptionalReducerData }
  | {
      type: 'setCzenServiceIdForMember';
      serviceIdForMember: ServiceIdsForMember;
      reducer?: OptionalReducerData;
    }
  | { type: 'setCareDate'; careDate: CARE_DATES; reducer?: OptionalReducerData }
  | {
      type: 'setCareCostImportance';
      careCostImportance: DAY_CARE_COST_IMPORTANCE;
      reducer?: OptionalReducerData;
    }
  | { type: 'setCareKind'; careKind: CareKind; reducer?: OptionalReducerData }
  | {
      type: 'setAdditionalVerticals';
      additionalVerticals: Array<ServiceType>;
      reducer?: OptionalReducerData;
    }
  | {
      type: 'setShowChildDetails';
      showChildDetails: boolean;
      reducer?: OptionalReducerData;
    }
  | { type: 'setIdealCaregiverQualities'; value: Array<string>; reducer?: OptionalReducerData }
  | {
      type: 'setPersonalityCustomContentTwo';
      personalityCustomContentTwo: string;
      reducer?: OptionalReducerData;
    }
  | {
      type: 'setCargiverAttributes';
      value: Array<string>;
      reducer?: OptionalReducerData;
    }
  | { type: 'setNeedHelpWith'; value: Array<string>; reducer?: OptionalReducerData }
  | {
      type: 'setCareChildren';
      careChildren: Child[];
      careExpecting: boolean;
      distanceLearning: boolean;
      reducer?: OptionalReducerData;
    }
  | {
      type: 'setCareChildrenDOB';
      careChildrenDOB: ChildDOB[];
      careExpecting: boolean;
      distanceLearning: boolean;
      reducer?: OptionalReducerData;
    }
  | {
      type: 'setCareAttributesCC';
      careAttributesCC: CareAttributesCC[];
      reducer?: OptionalReducerData;
    }
  | { type: 'setNumberOfChildren'; numberOfChildren: number; reducer?: OptionalReducerData }
  | { type: 'setCaregiverCount'; caregiverCount: number; reducer?: OptionalReducerData }
  | { type: 'cc_setEnrollmentSource'; value: string; reducer?: OptionalReducerData }
  | { type: 'cc_setDayCareDayTime'; dayTime: CareDayTime; reducer?: OptionalReducerData }
  | {
      type: 'cc_setSeekerName';
      firstName?: string;
      lastName?: string;
      reducer?: OptionalReducerData;
    }
  | { type: 'cc_setSeekerPhoneNumber'; phoneNumber: string; reducer?: OptionalReducerData }
  | {
      type: 'cc_setSeekerPhoneVerificationMethod';
      phoneVerificationMethod: PhoneVerificationChannel;
      reducer?: OptionalReducerData;
    }
  | { type: 'cc_setSeekerEmail'; email: string; reducer?: OptionalReducerData }
  | {
      type: 'cc_setSeekerContactMethod';
      contactMethod?: SeekerContactMethod;
      reducer?: OptionalReducerData;
    }
  | { type: 'setDayCareFrequency'; careType: string; reducer?: OptionalReducerData }
  | { type: 'setDayCareSpecificDays'; specificDays: DayOfWeek[]; reducer?: OptionalReducerData }
  | { type: 'setDayCareStartMonth'; month: string | null; reducer?: OptionalReducerData }
  | {
      type: 'setDayCareChildrenDateOfBirth';
      childrenDateOfBirth: ChildDateOfBirth[];
      reducer?: OptionalReducerData;
    }
  | {
      type: 'setAdditionalInformation';
      additionalInformation: string;
      reducer?: OptionalReducerData;
    }
  | {
      type: 'cc_setDayCareRecommendations';
      dayCareRecommendations: DaycareProviderProfile[];
      isExpanded: boolean;
      reducer?: OptionalReducerData;
    }
  | {
      type: 'cc_setDayCareRecommendationsSelectections';
      dayCareIds: string[];
      reducer?: OptionalReducerData;
    }
  | {
      type: 'cc_setDayCareRecommendationsSubmissionInfo';
      completed: boolean;
      reducer?: OptionalReducerData;
    }
  | { type: 'cc_startDayCareAutoSubmitCountdown'; reducer?: OptionalReducerData }
  | { type: 'cc_increaseDayCareAutoSubmitAttempt'; reducer?: OptionalReducerData }
  | { type: 'cc_autoSubmitDayCares'; reducer?: OptionalReducerData }
  | { type: 'setEnrollmentVariant'; variant: string; reducer?: OptionalReducerData }
  | {
      type: 'cc_setDayCareRecommendationsShouldShowMap';
      shouldShowMap: boolean;
      reducer?: OptionalReducerData;
    }
  | { type: 'cc_setIsSendLeadEnabled'; isSendLeadEnabled: boolean; reducer?: OptionalReducerData }
  | {
      type: 'cc_setDayCareRecommendationsTrackingId';
      trackingId: string;
      reducer?: OptionalReducerData;
    }
  | { type: 'setIsPixelFired'; isPixelFired: boolean; reducer?: OptionalReducerData }
  | {
      type: 'cc_setDayCareLeadIds';
      leadProviderAssociations: { leadId: string; providerId: string }[];
      reducer?: OptionalReducerData;
    }
  | {
      type: 'cc_setProviderTourRequestBooked';
      providerId: string;
      reducer?: OptionalReducerData;
    };

// Tax calculator page
export type UserInfoTaxCalculator = {
  location: Location;
  kidsNumber: number;
  hoursNumber: number;
};

export type CardDetailsTaxCalculator = {
  key: string;
  title: string;
  icon: ReactNode;
  link: string;
}[];

export type Money = {
  amount: string;
  currencyCode: string;
};

export type CardsDataTaxCalculator = {
  originalTotal: Money;
  discountedTotal: Money;
  savingPercentage: number;
  savingAmount: Money;
  subServiceType: string;
}[];
